import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Description,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { InputFieldOptions, ProvinceDropdown } from "./InputField";
import { AiOutlineCheckCircle, AiOutlineEdit } from "react-icons/ai"; // Import the icon
import { ModalTitle } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Conferma Azione",
  message = "Sei sicuro di voler procedere?",
  confirmLabel = "Elimina",
  cancelLabel = "Annulla",
  iconColor = "text-red-600", // Default color for delete actions
  iconBgColor = "bg-red-100", // Default background color for delete actions
  confirmBgColor = "bg-red-500",
  confirmHoverBgColor = "hover:bg-red-600",
  confirmTextColor = "text-white",
  cancelBgColor = "bg-white",
  cancelHoverBgColor = "hover:bg-gray-100",
  cancelTextColor = "text-gray-900",
}) => {
  if (!isOpen) return null;

  return (
    <Dialog className="relative z-10" open={isOpen} onClose={onClose}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto mt-10">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div
                  className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconBgColor} sm:mx-0 sm:h-10 sm:w-10`}
                >
                  <ExclamationTriangleIcon
                    className={`h-6 w-6 ${iconColor}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    {title}
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{message}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                className={`inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold ${confirmTextColor} shadow-sm ${confirmBgColor} hover:${confirmHoverBgColor} sm:ml-3 sm:w-auto`}
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
              <button
                type="button"
                className={`mt-3 inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold ${cancelTextColor} shadow-sm ring-1 ring-inset ring-gray-300 ${cancelBgColor} hover:${cancelHoverBgColor} sm:mt-0 sm:w-auto`}
                onClick={onClose}
              >
                {cancelLabel}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export const ModifyUser = ({
  userData,
  isOpen,
  onClose,
  onConfirm,
  onChange,
  handleUpdate,
  errorMessage,
  setErrorMessage,
  title = "Conferma Azione",
  message = "Sei sicuro di voler procedere?",
  confirmLabel,
  cancelLabel,
  iconColor = "text-red-600", // Default color for delete actions
  iconBgColor = "bg-red-100", // Default background color for delete actions
  confirmBgColor = "bg-red-500",
  confirmHoverBgColor = "hover:bg-red-600",
  confirmTextColor = "text-white",
  cancelBgColor = "bg-white",
  cancelHoverBgColor = "hover:bg-gray-100",
  cancelTextColor = "text-gray-900",
}) => {
  const [isModalOpen, setIsConfirmationModalOpen] = useState(false);
  const handleOpenModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmModal = () => {
    // Chiudi solo il modale di conferma
    setIsConfirmationModalOpen(false);
  };

  const handleDeleteAccount = async () => {
    axios.defaults.withCredentials = true;

    // Aggiungi qui la logica per la cancellazione dell'account

    try {
      const response = await axios.get(
        `${API_URL}/admin/deleteUser/${userData.id}`
      );
      handleCloseConfirmModal(); // Chiudi il modale di conferma
      handleCloseMainModal(); // Chiudi anche il modale principale
      window.location.reload();
    } catch (err) {
      console.log(err);
      return Promise.reject(err);
    }
  };

  if (!isOpen) return null;

  console.log("userData:", userData);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onConfirm();
    }
  };

  const handleCloseMainModal = () => {
    // Chiudi solo il modale principale
    onClose();
  };

  const ruoli = ["user", "admin"];

  return (
    <Dialog
      className="relative z-10 "
      open={isOpen}
      onClose={handleCloseMainModal}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-800 bg-opacity-50 transition-opacity duration-300 ease-in-out "
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto mt-10">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-lg transition-all sm:my-8 sm:w-full sm:max-w-lg"
          >
            <div className="bg-white px-6 py-4 sm:p-6">
              <div className="flex items-start">
                <div className="w-full">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-bold leading-6 text-gray-900 mb-4"
                  >
                    {title}
                  </DialogTitle>
                  <div className="mt-2">
                    {errorMessage && (
                      <div className="my-4 p-3 border border-red-500 rounded-md bg-red-100 shadow-md flex items-center">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-500 mr-3"
                          aria-hidden="true"
                        />
                        <p className="text-red-600 text-md font-medium">
                          {errorMessage}
                        </p>
                      </div>
                    )}
                    <form className="space-y-6" onSubmit={handleUpdate}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Ruolo
                          </label>
                          <select
                            id="ruolo"
                            value={userData.ruolo || ""}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                ruolo: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          >
                            {ruoli.map((ruolo) => (
                              <option key={ruolo} value={ruolo}>
                                {ruolo}
                              </option>
                            ))}
                          </select>
                        </div>
                        <InputFieldOptions
                          label="Nome"
                          id="nome"
                          value={userData.nome}
                          onKeyDown={handleKeyDown}
                          onChange={(e) =>
                            onChange((prevData) => ({
                              ...prevData,
                              nome: e.target.value,
                            }))
                          }
                        />
                        <InputFieldOptions
                          label="Cognome"
                          id="cognome"
                          value={userData.cognome}
                          onKeyDown={handleKeyDown}
                          onChange={(e) =>
                            onChange((prevData) => ({
                              ...prevData,
                              cognome: e.target.value,
                            }))
                          }
                        />
                        <div className="col-span-2">
                          <InputFieldOptions
                            label="Email"
                            id="email"
                            value={userData.email}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <InputFieldOptions
                            label="Numero di telefono"
                            id="num_tel"
                            value={userData.num_tel}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                num_tel: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="mt-6">
                        <h4 className="text-lg font-semibold">
                          Indirizzo di spedizione
                        </h4>
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <InputFieldOptions
                            label="Indirizzo"
                            id="indirizzo"
                            value={userData.indirizzo}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                indirizzo: e.target.value,
                              }))
                            }
                          />

                          <InputFieldOptions
                            label="Cap"
                            id="cap"
                            value={userData.cap}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                cap: e.target.value,
                              }))
                            }
                          />

                          <InputFieldOptions
                            label="Comune"
                            id="comune"
                            value={userData.comune}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                comune: e.target.value,
                              }))
                            }
                          />
                          <ProvinceDropdown
                            label="Provincia"
                            id="provincia"
                            value={userData.provincia}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                provincia: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:flex sm:justify-between sm:px-6">
              <button
                type="button"
                onClick={handleOpenModal}
                className="inline-flex w-full justify-center sm:w-auto px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-500 rounded-md hover:bg-red-500 hover:text-white transition"
              >
                Elimina account
              </button>
              <div className="mt-3 sm:mt-0 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`inline-flex w-full justify-center rounded-md sm:ml-3 sm:w-auto px-3 py-2 text-sm font-semibold ${confirmTextColor} shadow-sm ${confirmBgColor} ${confirmHoverBgColor}  text-white transition duration-300`}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </button>
                <button
                  type="button"
                  className={`inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold ${cancelTextColor} shadow-sm ring-1 ring-inset ring-gray-300 ${cancelBgColor} ${cancelHoverBgColor} sm:mt-0 sm:w-auto transition duration-300`}
                  onClick={handleCloseMainModal}
                >
                  {cancelLabel}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteAccount}
        title="Conferma Azione"
        message="Sei sicuro di voler disattivare il tuo account? Tutti i dati verranno rimossi in modo permanente. Questa azione non può essere annullata."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        iconColor="text-red-600"
        iconBgColor="bg-red-100"
        confirmBgColor="bg-red-500"
        confirmHoverBgColor="hover:bg-red-600"
        confirmTextColor="text-white"
      />
    </Dialog>
  );
};

export const ModifySubscription = ({
  userData,
  tipiAbbonamento,

  isOpen,
  onClose,
  onConfirm,
  onChange,
  handleUpdate,
  errorMessage,
  setErrorMessage,
  title = "Conferma Azione",
  message = "Sei sicuro di voler procedere?",
  confirmLabel = "Elimina",
  cancelLabel = "Annulla",
  iconColor = "text-red-600", // Default color for delete actions
  iconBgColor = "bg-red-100", // Default background color for delete actions
  confirmBgColor = "bg-red-500",
  confirmHoverBgColor = "hover:bg-red-600",
  confirmTextColor = "text-white",
  cancelBgColor = "bg-white",
  cancelHoverBgColor = "hover:bg-gray-100",
  cancelTextColor = "text-gray-900",
}) => {
  const [isModalOpen, setIsConfirmationModalOpen] = useState(false);
  const handleOpenModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmModal = () => {
    // Chiudi solo il modale di conferma
    setIsConfirmationModalOpen(false);
  };

  const handleDeleteSubscription = async () => {
    console.log("QUI");
    axios.defaults.withCredentials = true;

    // Aggiungi qui la logica per la cancellazione dell'account

    try {
      console.log("qui2");
      console.log(API_URL);
      /*const response = await axios.delete(
        `${API_URL}/admin/deleteSubscription/${userData.id}`
      );
      */
      console.log(API_URL + "/admin/deleteSubscription/" + userData.id);
      const response = await axios.post(
        `${API_URL}/admin/deleteSubscription/${userData.id}`
      );
      console.log("QUI3");
      handleCloseConfirmModal(); // Chiudi il modale di conferma
      handleCloseMainModal(); // Chiudi anche il modale principale
      window.location.reload();
    } catch (err) {
      console.log(err);
      return Promise.reject(err);
    }
  };

  if (!isOpen) return null;

  console.log("userData aggiunta utente:", userData);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onConfirm();
    }
  };

  const handleCloseMainModal = () => {
    // Chiudi solo il modale principale
    onClose();
  };

  const ruoli = ["user", "admin"];

  return (
    <Dialog
      className="relative z-10"
      open={isOpen}
      onClose={handleCloseMainModal}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto mt-10">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    Modifica Abbonamento
                  </DialogTitle>
                  <div className="mt-2">
                    {errorMessage && (
                      <div className="my-4 p-2 border border-red-400 rounded-lg bg-red-100 shadow-md flex items-center justify-center w-full transition duration-300 ease-in-out transform">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-500 text-2xl mr-3"
                          aria-hidden="true"
                        />
                        <p className="text-red-600 text-lg font-semibold">
                          {errorMessage}
                        </p>
                      </div>
                    )}
                    {/* Controllo Abbonamento */}

                    {userData.status === "attivo" ? (
                      <div className="text-green-600 font-semibold">
                        Abbonamento attivo fino al{" "}
                        {userData.data_fine
                          ? new Date(userData.data_fine).toLocaleDateString()
                          : "N/A"}
                      </div>
                    ) : (
                      <div className="text-red-600 font-semibold">
                        Abbonamento scaduto il{" "}
                        {userData.data_fine
                          ? new Date(userData.data_fine).toLocaleDateString()
                          : "N/A"}
                      </div>
                    )}
                    {/* Form Modifica Account */}
                    <form className="space-y-6" onSubmit={handleUpdate}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
                        <div className="mt-4 col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Ruolo
                          </label>
                          <select
                            id="ruolo"
                            value={userData.ruolo}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                ruolo: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          >
                            {ruoli.map((ruolo) => (
                              <option key={ruolo} value={ruolo}>
                                {ruolo}
                              </option>
                            ))}
                          </select>
                        </div>
                        <InputFieldOptions
                          label="Nome"
                          id="nome"
                          value={userData.nome}
                          onKeyDown={handleKeyDown}
                          onChange={(e) =>
                            onChange((prevData) => ({
                              ...prevData,
                              nome: e.target.value,
                            }))
                          }
                        />
                        <InputFieldOptions
                          label="Cognome"
                          id="cognome"
                          value={userData.cognome}
                          onKeyDown={handleKeyDown}
                          onChange={(e) =>
                            onChange((prevData) => ({
                              ...prevData,
                              cognome: e.target.value,
                            }))
                          }
                        />
                        <div className="col-span-2">
                          <InputFieldOptions
                            label="Email"
                            id="email"
                            value={userData.email}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-span">
                          <InputFieldOptions
                            label="Numero di telefono"
                            id="num_tel"
                            value={userData.num_tel}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                num_tel: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      {/* Controllo Abbonamento */}
                      <div>
                        <h1 className="font-semibold">
                          Impostazioni abbonamenti
                        </h1>
                        {/* Selezione tipo di abbonamento */}
                        <div className="mt-4">
                          <label
                            htmlFor="subscriptionType"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Seleziona tipo di abbonamento
                          </label>
                          <select
                            id="subscriptionType"
                            value={userData.id_prodotto}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                id_prodotto: e.target.value,
                              }))
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          >
                            <option value="">Seleziona un'opzione</option>
                            {tipiAbbonamento.map((tipo) => (
                              <option key={tipo.id} value={tipo.id}>
                                {tipo.titolo}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {" "}
                          <InputFieldOptions
                            type="date"
                            label="Data inizio abbonamento"
                            id="data_inizio" // Corrected typo here
                            value={userData.data_inizio} // Ensure the value is in "YYYY-MM-DD" format
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                data_inizio: e.target.value, // Make sure the input format is correct
                              }))
                            }
                          />
                          <InputFieldOptions
                            type="date"
                            label="Data fine abbonamento"
                            id="data_fine"
                            value={userData.data_fine}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                data_fine: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      {/* Sezione Indirizzo */}
                      <div>
                        <h1 className="font-semibold">
                          Indirizzo di spedizione
                        </h1>
                        <div className="grid grid-cols-2 gap-4">
                          <InputFieldOptions
                            label="Indirizzo"
                            id="indirizzo"
                            value={userData.indirizzo}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                indirizzo: e.target.value,
                              }))
                            }
                          />

                          <InputFieldOptions
                            label="Cap"
                            id="cap"
                            value={userData.cap}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                cap: e.target.value,
                              }))
                            }
                          />
                          <InputFieldOptions
                            label="Comune"
                            id="comune"
                            value={userData.comune}
                            onKeyDown={handleKeyDown}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                comune: e.target.value,
                              }))
                            }
                          />
                          <ProvinceDropdown
                            label="Provincia"
                            id="provincia"
                            value={userData.provincia}
                            onChange={(e) =>
                              onChange((prevData) => ({
                                ...prevData,
                                provincia: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-2 sm:flex sm:justify-between items-center sm:px-6">
              <button
                type="button"
                onClick={() => {
                  handleOpenModal();
                }}
                className="inline-flex w-full justify-center sm:w-auto px-4 py-2 text-sm font-semibold border-2 border-red-500 text-red-500 bg-white rounded-md hover:bg-red-500 hover:text-white transition duration-300"
              >
                Elimina abbonamento
              </button>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  className={`inline-flex w-full justify-center rounded-md sm:ml-3 sm:w-auto px-3 py-2 text-sm font-semibold ${confirmTextColor} shadow-sm ${confirmBgColor} ${confirmHoverBgColor}  text-white transition duration-300`}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </button>
                <button
                  type="button"
                  className={`inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold ${cancelTextColor} shadow-sm ring-1 ring-inset ring-gray-300 ${cancelBgColor} ${cancelHoverBgColor} sm:mt-0 sm:w-auto transition duration-300`}
                  onClick={handleCloseMainModal}
                >
                  {cancelLabel}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={isModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteSubscription}
        title="Conferma Azione"
        message="Sei sicuro di voler eliminare questo abbonamento? Tutti i dati verranno rimossi in modo permanente. Questa azione non può essere annullata."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        iconColor="text-red-600"
        iconBgColor="bg-red-100"
        confirmBgColor="bg-red-500"
        confirmHoverBgColor="hover:bg-red-600"
        confirmTextColor="text-white"
      />
    </Dialog>
  );
};

export const AddSubscription = ({
  users, // Lista utenti disponibile
  tipiAbbonamento,
  isOpen,
  onClose,
  onConfirm,
  onChange,
  handleUpdate,
  errorMessage,
  setErrorMessage,
  title = "Aggiungi Abbonamento",
  message = "Seleziona l'utente a cui vuoi aggiungere l'abbonamento.",
  confirmLabel = "Conferma",
  cancelLabel = "Annulla",
  iconColor = "text-green-600", // Icona per conferma azione
  iconBgColor = "bg-green-100", // Sfondo icona conferma
  confirmBgColor = "bg-green-500",
  confirmHoverBgColor = "hover:bg-green-600",
  confirmTextColor = "text-white",
  cancelBgColor = "bg-white",
  cancelHoverBgColor = "hover:bg-gray-100",
  cancelTextColor = "text-gray-900",
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState("");

  const userss = [
    { id_utente: 1, nome: "Mario", cognome: "Rossi", email: "mario@rossi.com" },
    {
      id_utente: 2,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    { id_utente: 3, nome: "Mario", cognome: "Rossi", email: "mario@rossi.com" },
    {
      id_utente: 4,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    { id_utente: 5, nome: "Mario", cognome: "Rossi", email: "mario@rossi.com" },
    {
      id_utente: 6,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    { id_utente: 7, nome: "Mario", cognome: "Rossi", email: "mario@rossi.com" },
    {
      id_utente: 8,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    { id_utente: 9, nome: "Mario", cognome: "Rossi", email: "mario@rossi.com" },
    {
      id_utente: 10,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    {
      id_utente: 11,
      nome: "Mario",
      cognome: "Rossi",
      email: "mario@rossi.com",
    },
    {
      id_utente: 12,
      nome: "Giulia",
      cognome: "Verdi",
      email: "giulia@verdi.com",
    },
    // Aggiungi altri utenti se necessario
  ];

  const handleUserChange = (e) => {
    const selectedValue = e.target.value;
    const user = users.find(
      (user) => user.id_utente === parseInt(selectedValue, 10)
    ); // Converti in numero
    setSelectedUser(user); // Aggiorna lo stato
  };

  useEffect(() => {
    console.log("USERS: ", users);
  }, [users]);

  // Funzioni per gestione modale di conferma
  const handleOpenModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmModal = () => setIsConfirmationModalOpen(false);
  const handleCloseMainModal = () => {
    setSearchTerm("");
    setErrorMessage(null);
    setSelectedUser("");
    onClose();
  };

  // Funzione per l'aggiunta dell'abbonamento
  const handleAddSubscription = async () => {
    if (!selectedUser) {
      setErrorMessage("Seleziona un utente prima di procedere.");
      return;
    }

    try {
      // Logica per aggiungere l'abbonamento all'utente selezionato
      await axios.post(`${API_URL}/admin/addSubscription`, {
        userId: selectedUser.id_utente,
        subscriptionData: selectedSubscription, // Dati dell'abbonamento da inviare
      });

      // Chiusura dei modali e azioni di conferma
      handleCloseConfirmModal();
      handleCloseMainModal();
      onConfirm(); // Esegui eventuali altre azioni di conferma
    } catch (err) {
      console.error("Errore durante l'aggiunta dell'abbonamento:", err);
      setErrorMessage("Errore durante l'aggiunta dell'abbonamento.");
    }
  };

  const handleSubscriptionChange = (e) => {
    console.log("Selected Subscription:", e.target.value);
    setSelectedSubscription(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value); // Aggiorna il termine di ricerca
  };

  // Filtra gli utenti in base al termine di ricerca
  const filteredUsers = users.filter((user) =>
    `${user.nome} ${user.cognome} ${user.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  if (!isOpen) return null;

  return (
    <Dialog
      className="relative z-10 "
      open={isOpen}
      onClose={handleCloseMainModal}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
      />
      <div className="fixed inset-0 z-10 overflow-y-auto mt-10">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl max-h-lg"
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    {title}
                  </DialogTitle>
                  <div className="mt-2">
                    {errorMessage && (
                      <div className="my-4 p-2 border border-red-400 rounded-lg bg-red-100 shadow-md flex items-center justify-center w-full transition duration-300 ease-in-out transform">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-500 text-2xl mr-3"
                          aria-hidden="true"
                        />
                        <p className="text-red-600 text-lg font-semibold">
                          {errorMessage}
                        </p>
                      </div>
                    )}
                    <p>{message}</p>

                    {/* Campo di ricerca */}
                    <div className="mt-4">
                      <label
                        htmlFor="searchInput"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Cerca Utente
                      </label>
                      <input
                        id="searchInput"
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Cerca per nome, cognome o email"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      />
                    </div>

                    {/* Selezione tipo di abbonamento */}
                    <div className="mt-4">
                      <label
                        htmlFor="subscriptionType"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Seleziona tipo di abbonamento
                      </label>
                      <select
                        id="subscriptionType"
                        value={selectedSubscription}
                        onChange={handleSubscriptionChange}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                        <option value="">Seleziona un'opzione</option>
                        {tipiAbbonamento.map((tipo) => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.titolo}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Informazioni sull'utente selezionato */}
                    {selectedUser && (
                      <div className="mt-4 p-4 bg-gray-100 rounded-md">
                        <h3 className="text-lg font-medium">
                          Utente Selezionato:
                        </h3>
                        <p>Nome: {selectedUser.nome}</p>
                        <p>Cognome: {selectedUser.cognome}</p>
                        <p>Email: {selectedUser.email}</p>
                      </div>
                    )}

                    {/* Tabella di selezione utenti */}
                    <div className="flex mt-4 min-w-xl max-h-72 overflow-y-scroll overflow-x-hidden bg-red-500">
                      <table className="min-w-full bg-white border border-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Seleziona
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Nome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Cognome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredUsers.map((user) => (
                            <tr
                              key={user.id_utente}
                              className={`cursor-pointer hover:bg-gray-100 transition duration-200 ease-in-out ${
                                selectedUser &&
                                selectedUser.id_utente === user.id_utente
                                  ? "bg-gray-100"
                                  : ""
                              }`}
                              onClick={() => handleUserSelect(user)}
                            >
                              <td className="px-1 py-6 whitespace-nowrap flex justify-center items-center">
                                <input
                                  type="radio"
                                  name="selectedUser"
                                  checked={
                                    selectedUser &&
                                    selectedUser.id_utente === user.id_utente
                                  }
                                  onChange={() => handleUserSelect(user)}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {user.nome}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {user.cognome}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {user.email}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                className={`inline-flex w-full justify-center rounded-md sm:ml-3 sm:w-auto px-3 py-2 text-sm font-semibold ${confirmTextColor} shadow-sm ${confirmBgColor} ${confirmHoverBgColor} text-white transition duration-300`}
                onClick={handleAddSubscription}
              >
                {confirmLabel}
              </button>
              <button
                type="button"
                className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold ${cancelTextColor} shadow-sm ring-1 ring-inset ring-gray-300 ${cancelBgColor} ${cancelHoverBgColor} sm:mt-0 sm:w-auto transition duration-300`}
                onClick={handleCloseMainModal}
              >
                {cancelLabel}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export const OrderDetails = ({
  orderData,
  isOpen,
  onClose,
  onConfirm,
  onChange,
  handleUpdate,
  errorMessage,
  setErrorMessage,
  title = "Conferma Azione",
  message = "Sei sicuro di voler procedere?",
  confirmLabel = "Elimina",
  cancelLabel = "Annulla",
  iconColor = "text-red-600", // Default color for delete actions
  iconBgColor = "bg-red-100", // Default background color for delete actions
  confirmBgColor = "bg-red-500",
  confirmHoverBgColor = "hover:bg-red-600",
  confirmTextColor = "text-white",
  cancelBgColor = "bg-white",
  cancelHoverBgColor = "hover:bg-gray-100",
  cancelTextColor = "text-gray-900",
}) => {
  useEffect(() => {
    console.log(orderData);
  }, [orderData]);
  if (!isOpen) return null;

  const handleCloseMainModal = () => {
    // Chiudi solo il modale principale
    onClose();
  };

  const ruoli = ["user", "admin"];
  return (
    <Dialog
      className="relative z-10"
      open={isOpen}
      onClose={handleCloseMainModal}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
          >
            {/* Header e messaggio di errore */}
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              {errorMessage && (
                <div className="my-4 p-2 border border-red-400 rounded-lg bg-red-100 shadow-md flex items-center justify-center w-full">
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-red-500 mr-3"
                    aria-hidden="true"
                  />
                  <p className="text-red-600 font-semibold">{errorMessage}</p>
                </div>
              )}
              {title && (
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900 text-center"
                >
                  {title}
                </DialogTitle>
              )}

              {/* Dettagli dell’ordine */}
              <div className="mt-5">
                {/* Link al pagamento e Checkbox ordine completato */}
                <div className="px-4 py-4  sm:flex sm:items-center sm:justify-between">
                  <a
                    href={`https://dashboard.stripe.com/test/payments/${orderData.id_pagamento}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    Link pagamento
                  </a>
                  <div className="flex items-center mt-3 sm:mt-0">
                    <input
                      type="checkbox"
                      id="completato"
                      checked={orderData.completato}
                      onChange={(e) => onChange(orderData.id)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="completato"
                      className="ml-2 text-sm text-gray-700"
                    >
                      Ordine spedito
                    </label>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">
                  ID Ordine: {orderData.codice_ordine || orderData.id}
                </h2>
                <p className="text-gray-600">
                  <span className="font-semibold">Data:</span>{" "}
                  {new Date(orderData.created_at).toLocaleDateString()}
                </p>

                {/* Prodotti dell’ordine */}
                <table className="w-full mt-5 text-left border-collapse">
                  <tbody>
                    {orderData.orderDetails?.map((rivista) => (
                      <tr
                        key={rivista.id}
                        className="border-t border-slate-200"
                      >
                        <td className="p-2 flex items-center">
                          <img
                            src={rivista.stripeProduct.images[0]}
                            alt={rivista.id}
                            className="w-20 h-auto mr-4"
                          />
                          <p className="text-gray-600">
                            {rivista.stripeProduct.name}
                          </p>
                        </td>
                        <td className="text-right p-2 text-gray-700">
                          €{rivista.prezzo}{" "}
                          <span className="text-sm text-gray-500">
                            (Qtà: {rivista.quantita})
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Informazioni pagamento e consegna */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <h3 className="font-semibold text-gray-800">Pagamento</h3>
                    <p>
                      {orderData.paymentMethod.brand +
                        " **" +
                        orderData.paymentMethod.last4}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Consegna</h3>
                    <p>{orderData.paymentDetails.address.line1}</p>
                    <p>{`${orderData.paymentDetails.address.city}, ${orderData.paymentDetails.address.state}, ${orderData.paymentDetails.address.country}`}</p>
                    {orderData.paymentDetails.phone && (
                      <p>{orderData.paymentDetails.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottoni azione */}
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:justify-end items-center gap-4">
              <button
                type="button"
                className={`ml-3 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold ${cancelTextColor} ${cancelBgColor} ${cancelHoverBgColor}`}
                onClick={handleCloseMainModal}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={`inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white ${confirmBgColor} ${confirmHoverBgColor}`}
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmationDialog;
